/**
 * The crafting calculation engine.
 *
 * Pure functions over the normalized data model — no React, no fetch, no
 * Bungie shapes. This is the layer the spec asks to be independently testable,
 * and it is where every number shown in the UI comes from.
 *
 * Bump ENGINE_VERSION when a rule changes in a way that would alter a saved
 * plan's computed cost.
 */
import type {
  CraftableWeapon,
  CraftingComponent,
  CraftingSocket,
  CraftingSocketKey,
  PlannerMode,
  ResourceAmount,
  WeaponConfiguration,
} from "./types.js";
import { SOCKET_ORDER } from "./types.js";
import {
  DEFAULT_PROGRESSION_ESTIMATE,
  PROVISIONAL_BASE_SHAPE_COST,
  type ProgressionEstimateConfig,
} from "./provisional.js";

export const ENGINE_VERSION = 1;

// --- selection ---------------------------------------------------------------

export interface SelectedComponent {
  key: CraftingSocketKey;
  socket: CraftingSocket;
  component: CraftingComponent;
}

export function findSocket(
  weapon: CraftableWeapon,
  key: CraftingSocketKey
): CraftingSocket | undefined {
  return weapon.sockets.find((s) => s.key === key);
}

/**
 * "Empty Barrels Socket" and friends: the manifest's "no component" option.
 * Real and selectable, but it is not a perk — the sync script sorts these to the
 * front of each column, and the UI gives them an outline rather than an icon.
 */
export function isEmptySocketComponent(component: { name: string }): boolean {
  return /^empty\b.*\bsocket$/i.test(component.name);
}

export function findComponent(
  weapon: CraftableWeapon,
  key: CraftingSocketKey,
  plugHash: number | undefined
): CraftingComponent | undefined {
  if (plugHash === undefined) return undefined;
  return findSocket(weapon, key)?.options.find((o) => o.plugHash === plugHash);
}

/**
 * Resolve a configuration into the components it selects, in slot-bar order.
 * Silently drops selections whose plug is no longer present — that happens when
 * a Destiny update removes a perk from a plug set, and a stale saved plan should
 * degrade rather than throw. Use `findMissingSelections` to surface it.
 */
export function resolveSelection(
  weapon: CraftableWeapon,
  config: WeaponConfiguration
): SelectedComponent[] {
  const out: SelectedComponent[] = [];
  for (const key of SOCKET_ORDER) {
    const socket = findSocket(weapon, key);
    if (!socket) continue;
    const component = socket.options.find((o) => o.plugHash === config[key]);
    if (component) out.push({ key, socket, component });
  }
  return out;
}

/** Columns whose saved plug hash no longer exists in the current data. */
export function findMissingSelections(
  weapon: CraftableWeapon,
  config: WeaponConfiguration
): CraftingSocketKey[] {
  const missing: CraftingSocketKey[] = [];
  for (const key of SOCKET_ORDER) {
    const plugHash = config[key];
    if (plugHash === undefined) continue;
    if (!findComponent(weapon, key, plugHash)) missing.push(key);
  }
  return missing;
}

/**
 * Turn a crafted weapon instance's inserted plugs into a planner configuration.
 *
 * Matches by plug hash rather than by socket index. The instance's socket
 * indices belong to the *output weapon*, while our columns come from the
 * *recipe* — they happen to line up today, but relying on that would break
 * silently the first time Bungie inserts a socket. Searching each column's own
 * options for a hash the instance actually has is index-independent.
 *
 * Unrecognised plugs are ignored: mods, shaders, trackers and masterworks all
 * appear in the instance's socket list and none of them are craftable choices.
 */
export function configurationFromInstance(
  weapon: CraftableWeapon,
  instancePlugHashes: readonly number[]
): WeaponConfiguration {
  const inserted = new Set(instancePlugHashes.filter((h) => h > 0));
  const config: WeaponConfiguration = {};
  for (const socket of weapon.sockets) {
    const match = socket.options.find((option) => inserted.has(option.plugHash));
    if (match) config[socket.key] = match.plugHash;
  }
  return config;
}

// --- required level ----------------------------------------------------------

/**
 * The weapon level needed for a configuration: the highest required level among
 * its selected components. An empty selection requires level 0.
 */
export function requiredWeaponLevel(selected: SelectedComponent[]): number {
  let max = 0;
  for (const s of selected) {
    const level = s.component.requiredLevel;
    // Guard against absent/NaN requirements in partially-normalized data.
    if (Number.isFinite(level) && level > max) max = level;
  }
  return max;
}

/** The component responsible for the required level — what to blame for the grind. */
export function highestRequirementComponent(
  selected: SelectedComponent[]
): SelectedComponent | undefined {
  let best: SelectedComponent | undefined;
  for (const s of selected) {
    const level = s.component.requiredLevel;
    if (!Number.isFinite(level)) continue;
    if (!best || level > best.component.requiredLevel) best = s;
  }
  return best && best.component.requiredLevel > 0 ? best : undefined;
}

/** Whether every selected component is insertable at the given weapon level. */
export function isConfigurationAvailable(selected: SelectedComponent[], atLevel: number): boolean {
  return selected.every((s) => s.component.requiredLevel <= atLevel);
}

// --- progression -------------------------------------------------------------

export interface ProgressionSummary {
  currentProgressInLevels: number;
  requiredWeaponLevel: number;
  remainingLevels: number;
  /** Undefined when no estimate is meaningful (nothing left to grind). */
  estimatedKills?: number;
  estimatedActivities: { label: string; completions: number }[];
  alreadyAvailable: boolean;
}

/**
 * Total progress expressed in levels, e.g. level 11 at 63% -> 11.63.
 * `currentLevelPercent` is clamped to 0–99.99 as the spec requires: 100% would
 * mean the next level, which the game reports as a level change instead.
 */
export function currentProgressInLevels(
  currentWeaponLevel: number,
  currentLevelPercent: number
): number {
  const level = Number.isFinite(currentWeaponLevel) ? Math.max(0, currentWeaponLevel) : 0;
  const pct = Number.isFinite(currentLevelPercent)
    ? Math.min(99.99, Math.max(0, currentLevelPercent))
    : 0;
  return level + pct / 100;
}

export function remainingLevels(progressInLevels: number, targetLevel: number): number {
  return Math.max(0, (Number.isFinite(targetLevel) ? targetLevel : 0) - progressInLevels);
}

export function summarizeProgression(
  selected: SelectedComponent[],
  currentWeaponLevel: number,
  currentLevelPercent: number,
  config: ProgressionEstimateConfig = DEFAULT_PROGRESSION_ESTIMATE
): ProgressionSummary {
  const required = requiredWeaponLevel(selected);
  const progress = currentProgressInLevels(currentWeaponLevel, currentLevelPercent);
  const remaining = remainingLevels(progress, required);

  return {
    currentProgressInLevels: progress,
    requiredWeaponLevel: required,
    remainingLevels: remaining,
    estimatedKills:
      remaining > 0 ? Math.ceil(remaining * config.estimatedKillsPerLevel) : undefined,
    estimatedActivities:
      remaining > 0
        ? config.activityEstimates
            .filter((a) => a.progressPerCompletion > 0)
            .map((a) => ({
              label: a.label,
              completions: Math.ceil(remaining / a.progressPerCompletion),
            }))
        : [],
    alreadyAvailable: remaining === 0,
  };
}

// --- diffing -----------------------------------------------------------------

export interface ComponentChange {
  key: CraftingSocketKey;
  from?: CraftingComponent;
  to?: CraftingComponent;
}

/**
 * Columns where the planned configuration differs from the base. Columns absent
 * from the plan are treated as "unchanged", not "cleared" — the planner never
 * removes a component the user did not explicitly change.
 */
export function diffConfigurations(
  weapon: CraftableWeapon,
  base: WeaponConfiguration | undefined,
  planned: WeaponConfiguration
): ComponentChange[] {
  const changes: ComponentChange[] = [];
  for (const key of SOCKET_ORDER) {
    if (!findSocket(weapon, key)) continue;
    const plannedHash = planned[key];
    if (plannedHash === undefined) continue;
    const baseHash = base?.[key];
    if (baseHash === plannedHash) continue;
    changes.push({
      key,
      from: findComponent(weapon, key, baseHash),
      to: findComponent(weapon, key, plannedHash),
    });
  }
  return changes;
}

// --- resources ---------------------------------------------------------------

/** Sum resource lists, merging by itemHash and dropping non-positive amounts. */
export function aggregateResources(lists: readonly (readonly ResourceAmount[])[]): ResourceAmount[] {
  const totals = new Map<number, number>();
  for (const list of lists) {
    for (const { itemHash, quantity } of list) {
      if (!Number.isFinite(quantity) || quantity <= 0) continue;
      totals.set(itemHash, (totals.get(itemHash) ?? 0) + quantity);
    }
  }
  return [...totals].map(([itemHash, quantity]) => ({ itemHash, quantity }));
}

export interface CraftingCostResult {
  shapeNow: ResourceAmount[];
  reshapeLater: ResourceAmount[];
  total: ResourceAmount[];
}

export interface CostInput {
  weapon: CraftableWeapon;
  mode: PlannerMode;
  /**
   * `owned-reshape`: the weapon's real current configuration.
   * `new-shape`: the components the user intends to shape with *now*.
   * Omitted in `new-shape` means "shape the whole plan immediately".
   */
  baseConfiguration?: WeaponConfiguration;
  plannedConfiguration: WeaponConfiguration;
}

/**
 * Split a plan's resource cost into what it costs to shape now versus reshape
 * later.
 *
 * Unchanged components are never charged: reshaping only bills the columns whose
 * plug actually changes. That matches the game and is what the spec requires.
 */
export function calculateCost(input: CostInput): CraftingCostResult {
  const { weapon, mode, baseConfiguration, plannedConfiguration } = input;

  if (mode === "owned-reshape") {
    // The weapon already exists — there is nothing to shape, only the delta.
    const changed = diffConfigurations(weapon, baseConfiguration, plannedConfiguration);
    const reshapeLater = aggregateResources(changed.map((c) => c.to?.costs ?? []));
    return { shapeNow: [], reshapeLater, total: reshapeLater };
  }

  // new-shape: the initial shape defaults to the whole plan.
  const initial = baseConfiguration ?? plannedConfiguration;
  const shapeNow = aggregateResources([
    PROVISIONAL_BASE_SHAPE_COST,
    ...resolveSelection(weapon, initial).map((s) => s.component.costs),
  ]);
  const changed = diffConfigurations(weapon, initial, plannedConfiguration);
  const reshapeLater = aggregateResources(changed.map((c) => c.to?.costs ?? []));

  return {
    shapeNow,
    reshapeLater,
    total: aggregateResources([shapeNow, reshapeLater]),
  };
}

// --- inventory comparison ----------------------------------------------------

export interface ResourceSufficiency {
  itemHash: number;
  required: number;
  owned: number;
  shortfall: number;
  sufficient: boolean;
}

/**
 * Compare required resources against what the player holds. `owned` maps
 * itemHash -> quantity; a missing entry counts as zero, and `hasInventory`
 * distinguishes "you have none" from "we could not read your inventory".
 */
export function compareToInventory(
  required: readonly ResourceAmount[],
  owned: Readonly<Record<number, number>>
): ResourceSufficiency[] {
  return required.map(({ itemHash, quantity }) => {
    const have = owned[itemHash] ?? 0;
    return {
      itemHash,
      required: quantity,
      owned: have,
      shortfall: Math.max(0, quantity - have),
      sufficient: have >= quantity,
    };
  });
}
